<template>
  <div class="available-plans">
    <div class="available-plans__header">
      <h4 class="available-plans__title">Available Plans</h4>
      <div class="available-plans__toggle">
        <div class="available-plans__toggle-btn-wrapper">
          <button class="available-plans__toggle-btn" :class="{ 'available-plans__toggle-btn--active': billingCycle === 'monthly' }" @click="changeCycle('monthly')">Monthly</button>
          <button class="available-plans__toggle-btn" :class="{ 'available-plans__toggle-btn--active': billingCycle === 'yearly' }" @click="changeCycle('yearly')">Yearly</button>
        </div>
      </div>
    </div>

    <div v-if="!availablePlans || availablePlans.length === 0" class="available-plans__loading">
      <q-spinner-dots color="primary" size="40px" />
    </div>
    <div v-else class="available-plans__list">
      <div v-for="plan in availablePlans" :key="plan.id" class="available-plans__plan" :class="{ 'available-plans__plan--current': plan.id === currentPlanId || plan.is_subscribed }">
        <div class="available-plans__plan-header">
          <h5 class="available-plans__plan-name">{{ plan.name }}</h5>
          <div class="available-plans__plan-price">
            <!-- For Business plan with no price -->
            <template v-if="plan.isBusiness">
              <span class="available-plans__business-price">Talk to our team</span>
            </template>
            <!-- For plans with pricing -->
            <template v-else>
              <div class="available-plans__price-container">
                <span class="available-plans__plan-amount">{{ plan.price.amount | formatPrice }} {{ plan.price.currency }}</span>
                <span class="available-plans__plan-period">/ {{ plan.price.period }}</span>
                <div v-if="plan.discount" class="available-plans__discount">Saves {{ plan.discount }}</div>
              </div>
              <!-- Show original price for discounted plans -->
              <div v-if="plan.origin_price" class="available-plans__original-price">
                <span class="available-plans__original-amount">{{ plan.origin_price | formatPrice }} {{ plan.price.currency }}</span>
              </div>
            </template>
          </div>
          <p class="available-plans__plan-description">{{ plan.description }}</p>
        </div>

        <div class="available-plans__plan-features-wrapper">
          <div class="available-plans__plan-features">
            <div v-for="(feature, index) in plan.detailedFeatures || plan.features" :key="index" class="available-plans__plan-feature">
              <q-icon name="check_circle" size="16px" color="primary" class="q-mr-xs" />
              <span class="available-plans__feature-text">{{ feature }}</span>
            </div>
          </div>
        </div>

        <div class="available-plans__plan-action">
          <q-btn v-if="plan.id === currentPlanId" flat no-caps class="available-plans__subscribed-btn" disable>
            <q-icon name="check_circle" size="16px" class="q-mr-xs" />
            Subscribed
          </q-btn>
          <q-btn v-else-if="plan.isBusiness" flat no-caps class="available-plans__contact-btn" @click="$emit('contact')" :loading="isEnterpriseContactLoading" :disable="isEnterpriseContactLoading">
            <template v-slot:loading>
              <q-spinner-dots color="white" size="20px" />
            </template>
            Get in touch
          </q-btn>
          <q-btn v-else flat no-caps class="available-plans__upgrade-btn" @click="$emit('select-plan', plan)"> Subscribe </q-btn>
        </div>

        <div v-if="plan.is_popular" class="available-plans__popular-badge">Most popular</div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'AvailablePlans',
  props: {
    currentPlanId: {
      type: [String, Number],
      required: true
    },
    isEnterpriseContactLoading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters({
      availablePlans: 'billing/availablePlans',
      billingCycle: 'billing/billingCycle'
    })
  },
  methods: {
    ...mapActions({
      setBillingCycle: 'billing/setBillingCycle'
    }),
    changeCycle(value) {
      this.setBillingCycle(value);
    }
  }
};
</script>

<style lang="scss" scoped>
.available-plans {
  margin-top: 32px;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  }

  &__title {
    @include inter-font($size: 18px, $bolded: true, $color: var(--main-text-color));
    margin: 0;
  }

  &__toggle-btn-wrapper {
    display: flex;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    background-color: #f5f5f5;
  }

  &__toggle-btn {
    padding: 6px 16px;
    @include inter-font($size: 12px, $bolded: false, $color: var(--secondary-text-color));
    background: none;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    flex: 1;

    &--active {
      @include inter-font($size: 12px, $bolded: true, $color: var(--main-text-color));
      background-color: var(--terziary-btn-color);
    }

    &:focus {
      outline: none;
    }
  }

  &__loading {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
    background-color: rgba(0, 0, 0, 0.02);
    border-radius: 8px;
  }

  &__list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 24px;
    margin-top: 16px;
  }

  &__plan {
    background-color: #fff;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    padding: 28px;
    display: flex;
    flex-direction: column;
    position: relative;
    transition: all 0.2s ease;
    height: 100%;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
      transform: translateY(-2px);
    }

    &--current {
      border: 1px solid var(--positive);
    }
  }

  &__plan-header {
    margin-bottom: 16px;
  }

  &__plan-name {
    @include inter-font($size: 18px, $bolded: true, $color: var(--main-text-color));
    margin: 0 0 8px 0;
  }

  &__plan-price {
    display: flex;
    flex-direction: column;
    margin-bottom: 8px;
  }

  &__price-container {
    display: flex;
    align-items: baseline;
  }

  &__plan-amount {
    @include inter-font($size: 24px, $bolded: true, $color: var(--main-text-color));
  }

  &__plan-period {
    @include inter-font($size: 14px, $color: var(--secondary-text-color));
    margin-left: 4px;
  }

  &__original-price {
    margin-top: 4px;
  }

  &__original-amount {
    @include inter-font($size: 14px, $color: var(--secondary-text-color));
    text-decoration: line-through;
  }

  &__business-price {
    @include inter-font($size: 24px, $bolded: true, $color: var(--main-text-color));
  }

  &__discount {
    @include inter-font($size: 12px, $bolded: true, $color: var(--main-text-color), $weight: 600);
    background-color: var(--light-main-color);
    border-radius: 4px;
    padding: 2px 8px;
    margin-left: 8px;
    display: inline-block;
  }

  &__plan-description {
    @include inter-font($size: 14px, $color: var(--secondary-text-color));
    margin: 8px 0 0 0;
  }

  &__plan-features-wrapper {
    flex-grow: 1;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
  }

  &__plan-features {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__plan-feature {
    @include inter-font($size: 14px, $color: var(--main-text-color));
    display: flex;
    align-items: center;
    gap: 12px;

    .q-icon {
      min-width: 16px;
      flex-shrink: 0;
    }
  }

  &__feature-text {
    flex: 1;
    @include inter-font($size: 14px, $color: var(--main-text-color));
  }

  &__plan-action {
    margin-top: auto;
  }

  &__upgrade-btn,
  &__contact-btn,
  &__current-btn,
  &__subscribed-btn {
    width: 100%;
    @include inter-font($size: 14px, $bolded: true, $color: var(--main-text-color));
    padding: 8px 16px;
    border-radius: 8px;
  }

  &__upgrade-btn {
    background-color: var(--main-btn-color);
  }

  &__contact-btn {
    background-color: var(--main-btn-color);
  }

  &__current-btn {
    opacity: 0.7;
  }

  &__subscribed-btn {
    @include inter-font($size: 14px, $bolded: true, $color: var(--positive));
  }

  &__popular-badge {
    position: absolute;
    top: 12px;
    right: 12px;
    background-color: var(--light-main-color);
    border-radius: 4px;
    padding: 4px 8px;
    @include inter-font($size: 12px, $bolded: true, $color: var(--main-text-color));
  }
}
</style>
